import { useLocale } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SetURLSearchParams, useParams, useSearchParams } from 'react-router-dom';

import { GroupedOptions, SelectOption, SingleOption, explodeOptions, isSingleOption } from '../select-types';
import { ObjectSelectInputProps } from './object-select-input.types';
import { buildPaginatedFetchParams, canLoadNextPage, shouldBlockMinSearchCharacters } from './object-select-input.utils';

export { buildPaginatedFetchParams, canLoadNextPage, hasEnoughSearchCharacters, shouldBlockMinSearchCharacters } from './object-select-input.utils';

interface ViewProps<T extends { id: string }> {
    options: SelectOption<T>[];
    value?: T | null;
    onScrollToBottom: () => void;
    search: (value: string) => void;
    onSelect: (option: SingleOption<T> | null) => void;
    overwriteDisabled?: boolean;
    hide: boolean;
    isOpenOverwrite?: boolean;
    isClearable: boolean;
}

const pageSize = '100';

const useObjectSelectInput = <T extends { id: string }, D extends string | number>(props: ObjectSelectInputProps<T, D>): ViewProps<T> => {
    const { locale } = useLocale();
    const params = useParams();
    const { store } = useAccountRepository();
    const [urlOverwrite, changeUrlOverwrite] = useState<T | null>(null);
    const [cache, changeCache] = useState<T[]>([]);
    const timer = useRef<NodeJS.Timeout | null>(null);
    const [count, changeCount] = useState<number>(0);
    const [index, changeIndex] = useState<number>(0);
    const [searchNonce, setSearchNonce] = useState(0);
    const [options, changeOptions] = useState<SelectOption<T>[]>([]);
    const [mappedOptions, changeMappedOptions] = useState<GroupedOptions<T>[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [autofilled, setAutofilled] = useState(false);
    const activeSearchRef = useRef('');
    const indexRef = useRef(index);

    useEffect(() => {
        indexRef.current = index;
    }, [index]);
    const isClearable = props.isClearable ?? true;
    const value: T | null = useMemo(() => {
        if (isClearable && props.value === null && props.idValue === null) {
            props.onChange && props.onChange(null);
            return null;
        }
        const explodedOptions = explodeOptions(options);
        if (explodedOptions.length === 1 && props.required && autofilled === false) {
            const found = cache.find((o) => o.id === explodedOptions[0].value);
            if (!found) return null;
            if (props.onChange && props.value?.id !== found.id) {
                props.onChange(found);
                setAutofilled(true);
            }
            return found;
        }
        return props.value ? (props.value.id === props.idValue ? props.value : (cache.find((o) => o.id === props.idValue) ?? null)) : null;
    }, [props.value, props.idValue, options]);

    useEffect(() => {
        const param = searchParams.get(props.useQueryParam?.param ?? '');
        if (!urlOverwrite && props.useQueryParam) {
            if (!param && !props.value) return;
            onQueryParamChange(value?.id ?? null, props.useQueryParam?.param ?? '', setSearchParams);
        }
    }, [value]);

    useEffect(() => {
        if (!props.idValue) return;
        if (props.singleFetch && options && options.length > 0 && !explodeOptions(options).find((o) => o.value == props.idValue)) {
            props.singleFetch(props.idValue).then((res) => {
                if (!res) return;
                changeOptions((prevOptions) => {
                    const exploded = explodeOptions(prevOptions);
                    const found = exploded.find((o) => o.value === res.id);
                    if (found) return prevOptions;
                    else return [...prevOptions, { value: res.id, label: props.itemLabel(res) }];
                });
                changeCache((prevCache) => [...prevCache, res]);
            });
        }
    }, [props.idValue, options, locale]);

    const onSelect = (option: SingleOption<T> | null) => {
        if (option && option.metadata) {
            props.onChange && props.onChange(option.metadata);
        } else {
            const object = cache.find((o) => o.id === option?.value);
            props.onChange && props.onChange(object ?? null);
        }
    };

    useEffect(() => {
        if (props.idValue && props.value == undefined && value == null && props.onChange) {
            const found = cache.find((o) => o.id === props.idValue);
            if (found) props.onChange(found);
        }
    }, [options]);

    useEffect(() => {
        if (props.useUrlOverwrite && !props.disableFetch) {
            const param = params[props.useUrlOverwrite.param];
            if (param !== undefined)
                props.useUrlOverwrite.fetch(param).then((result) => {
                    changeUrlOverwrite(result);
                    props.onChange && props.onChange(result);
                });
        }
    }, [props.useUrlOverwrite]);

    useEffect(() => {
        const queryParam = searchParams.get(props.useQueryParam?.param ?? '');
        if (props.useQueryParam && value === null && queryParam !== null && !props.disableFetch) {
            props.useQueryParam.fetch(queryParam).then((result) => {
                props.onChange && props.onChange(result);
            });
        }
    }, [onQueryParamChange]);

    const queryParamsString = JSON.stringify(props.queryParams);
    const previousQueryParamsString = usePrevious(queryParamsString);

    // Reset index when queryParams change
    useEffect(() => {
        if (previousQueryParamsString !== undefined && previousQueryParamsString !== queryParamsString) {
            changeIndex(0);
        }
    }, [queryParamsString, previousQueryParamsString]);

    const previous = usePrevious({ index: index, account: store.currentAccount, searchNonce });
    useEffect(() => {
        if (!store.currentAccount) return;
        if (props.disableFetch) return;
        if (
            previous == undefined ||
            previousQueryParamsString !== queryParamsString ||
            previous.index != index ||
            previous.account != store.currentAccount ||
            previous.searchNonce != searchNonce ||
            !props.value
        ) {
            const fetchParams = buildPaginatedFetchParams(props.queryParams, {
                index: index.toString(),
                size: pageSize,
                accountId: store.currentAccount,
                search: activeSearchRef.current
            });

            const applyPageResults = (result: { count: number; results: T[] }, prepend?: T) => {
                const dedupedResults = dedupeResultSetById(prepend ? [prepend, ...result.results] : result.results);
                changeCount(result.count);
                if (index == 0) {
                    if (!props.mapOptions) {
                        changeOptions(mapResultSetToOptions(dedupedResults, props.itemLabel));
                        changeCache(dedupedResults);
                    } else {
                        changeMappedOptions(mapResultSetToMappedOptions(dedupedResults, props.mapOptions, props.itemLabel));
                        changeCache(dedupedResults);
                    }
                } else {
                    if (!props.mapOptions) {
                        changeOptions((options ?? []).concat(mapResultSetToOptions(dedupedResults, props.itemLabel)));
                        changeCache(dedupeResultSetById((cache ?? []).concat(dedupedResults)));
                    } else {
                        changeMappedOptions((mappedOptions ?? []).concat(mapResultSetToMappedOptions(dedupedResults, props.mapOptions, props.itemLabel)));
                        changeCache(dedupeResultSetById((cache ?? []).concat(dedupedResults)));
                    }
                }
            };

            if (props.singleFetch && props.idValue) {
                props.singleFetch(props.idValue).then((singleResult) => {
                    if (!singleResult) {
                        props.fetch(fetchParams).then((result) => applyPageResults(result));
                    } else {
                        props.fetch(fetchParams).then((result) => applyPageResults(result, singleResult));
                    }
                });
            } else {
                props.fetch(fetchParams).then((result) => applyPageResults(result));
            }
        }
    }, [index, queryParamsString, store.currentAccount, locale, searchNonce]);

    useEffect(() => {
        // Keep previous option references when labels are unchanged so an unstable
        // itemLabel callback (new fn each render) cannot infinite-loop via setState.
        if (props.mapOptions) {
            const next = mapResultSetToMappedOptions(cache, props.mapOptions, props.itemLabel);
            changeMappedOptions((prev) => (areGroupedOptionsEqual(prev, next) ? prev : next));
        } else {
            const next = mapResultSetToOptions(cache, props.itemLabel);
            changeOptions((prev) => (areSingleOptionsEqual(prev, next) ? prev : next));
        }
    }, [props.itemLabel, props.mapOptions, locale, cache]);

    const clearOptions = () => {
        changeOptions([]);
        changeMappedOptions([]);
        changeCache([]);
    };

    const resetPaginationForSearch = () => {
        if (indexRef.current !== 0) {
            changeIndex(0);
        } else {
            setSearchNonce((nonce) => nonce + 1);
        }
    };

    const search = (value: string) => {
        if (props.disableFetch) return;
        if (timer.current) {
            clearTimeout(timer.current);
        }

        const trimmed = value.trim();

        // minSearchCharacters is opt-in (e.g. delayed_receivers_search); off by default.
        if (shouldBlockMinSearchCharacters(trimmed, props.minSearchCharacters)) {
            activeSearchRef.current = '';
            clearOptions();
            changeCount(0);
            if (indexRef.current !== 0) {
                changeIndex(0);
            }
            return;
        }

        timer.current = setTimeout(() => {
            activeSearchRef.current = trimmed;
            resetPaginationForSearch();
        }, 500);
    };

    const scrollBottomVariableRef = useRef({ count, pageSize, index });
    useEffect(() => {
        scrollBottomVariableRef.current = { count, pageSize, index };
    }, [count, pageSize, index]);
    const onScrollToBottom = () => {
        const { count, pageSize, index } = scrollBottomVariableRef.current;
        if (canLoadNextPage(count, pageSize, index)) {
            changeIndex(index + 1);
        }
    };

    const opt = useMemo(() => {
        if (props.subset) {
            if (props.subset.length === 0) return [];
            if (props.mapOptions) {
                const result: { label: string; options: { value: string; label: string }[] }[] = [];
                mappedOptions.forEach((o) => {
                    const current: { label: string; options: { value: string; label: string }[] } = { label: o.label, options: [] };
                    o.options.filter((op) => props.subset!.includes(op.value)).forEach((op) => current.options.push(op));
                    if (current.options.length > 0) result.push(current);
                });
                return result;
            } else {
                return options.filter((o) => {
                    if (isSingleOption(o)) {
                        return props.subset!.includes(o.value);
                    }
                    return o.options.filter((op) => props.subset!.includes(op.value)).length > 0;
                });
            }
        } else {
            if (props.mapOptions) {
                return mappedOptions;
            } else {
                return options;
            }
        }
    }, [props.mapOptions, props.subset, options, mappedOptions]);

    return {
        options: opt,
        value,
        onScrollToBottom,
        search,
        onSelect,
        overwriteDisabled: urlOverwrite !== null || props.disableFetch,
        hide: props.hidden === true || (props.hideOnOneOption === true && options.length <= 1 && !isClearable),
        isOpenOverwrite: props.isOpenOverwrite,
        isClearable
    };
};

export default useObjectSelectInput;

export function mapResultSetToOptions<T extends { id: string }>(result: T[], labels: (item: T) => string): SingleOption<T>[] {
    return dedupeResultSetById(result).map((item) => ({
        value: item.id, //issue
        label: labels(item) ?? item.id,
        metadata: item
    }));
}

export function mapResultSetToMappedOptions<T extends { id: string }>(
    result: T[],
    mapKey: keyof T,
    labels: (item: T) => string
): { label: string; options: { value: any; label: string }[] }[] {
    const groupedItems: { label: string; options: { value: any; label: string }[] }[] = [];

    dedupeResultSetById(result).forEach((item) => {
        if (item[mapKey] && groupedItems.find((g) => g.label === item[mapKey])) {
            groupedItems.find((g) => g.label === item[mapKey])?.options.push({ value: item.id, label: labels(item) ?? item.id });
        } else {
            groupedItems.push({ label: item[mapKey] as string, options: [{ value: item.id, label: labels(item) ?? item.id }] });
        }
    });

    return groupedItems;
}

function dedupeResultSetById<T extends { id: string }>(result: T[]): T[] {
    const uniqueResults = new Map<string, T>();

    result.forEach((item) => {
        uniqueResults.set(item.id, item);
    });

    return Array.from(uniqueResults.values());
}

function areSingleOptionsEqual<T>(prev: SelectOption<T>[], next: SingleOption<T>[]): boolean {
    if (prev.length !== next.length) return false;
    return prev.every((option, i) => {
        if (!isSingleOption(option)) return false;
        return option.value === next[i].value && option.label === next[i].label;
    });
}

function areGroupedOptionsEqual<T>(prev: GroupedOptions<T>[], next: { label: string; options: { value: string; label: string }[] }[]): boolean {
    if (prev.length !== next.length) return false;
    return prev.every((group, i) => {
        if (group.label !== next[i].label || group.options.length !== next[i].options.length) return false;
        return group.options.every((option, j) => option.value === next[i].options[j].value && option.label === next[i].options[j].label);
    });
}

export function usePrevious<T>(value: T) {
    const ref = useRef<T>(null);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export const onQueryParamChange = (value: string | string[] | null, key: string, setSearchParams: SetURLSearchParams) => {
    if (value) {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    params.delete(key);
                    return params;
                }
                const currentValues = params.getAll(key);
                currentValues.forEach((v) => {
                    if (!value.includes(v)) {
                        params.delete(key, v);
                    }
                });
                value.forEach((v) => {
                    if (!currentValues.includes(v)) {
                        params.append(key, v);
                    }
                });
                return params;
            } else {
                if (params.has(key)) {
                    params.set(key, value);
                } else {
                    params.append(key, value);
                }
                return params;
            }
        });
    } else {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.delete(key);
            return params;
        });
    }
};
